import React from 'react';
import { TextField, Box } from '@mui/material';
import { Controller } from 'react-hook-form';
import type { AuditDynamicFormHeaderProps } from '../../types/auditFormTypes';
import { formatTimeHhMmSsInput } from '../../utils/formatters/timeFormatters';

const errorBorderSx = {
  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
    borderColor: 'error.main',
    borderWidth: '1px',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'error.main',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'error.main',
  },
};

const AuditDynamicFormHeader: React.FC<AuditDynamicFormHeaderProps> = React.memo(({
  control,
  errors,
  retailerLocked,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      mb: 2,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Controller
        name="shift_date"
        control={control}
        render={({ field }) => (
          <TextField
            label="Shift Date"
            size="small"
            {...field}
            error={!!errors.shift_date}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { style: { height: 30, padding: '0 8px' } },
            }}
            sx={{ mr: 1, ...(errors.shift_date ? errorBorderSx : {}) }}
          />
        )}
      />
      <Controller
        name="shift_start_time"
        control={control}
        render={({ field }) => (
          <TextField
            label="Start Time"
            size="small"
            required
            {...field}
            value={field.value ?? ''}
            onChange={(event) => field.onChange(formatTimeHhMmSsInput(event.target.value))}
            error={!!errors.shift_start_time}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: {
                style: { height: 30, padding: '0 8px' },
                'data-testid': 'start-time-input',
                inputMode: 'numeric',
                placeholder: 'HH:MM:SS',
              },
            }}
            sx={{ mr: 1, ...(errors.shift_start_time ? errorBorderSx : {}) }}
          />
        )}
      />
      <Controller
        name="shift_end_time"
        control={control}
        render={({ field }) => (
          <TextField
            label="End Time"
            size="small"
            required
            {...field}
            value={field.value ?? ''}
            onChange={(event) => field.onChange(formatTimeHhMmSsInput(event.target.value))}
            error={!!errors.shift_end_time}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: {
                style: { height: 30, padding: '0 8px' },
                'data-testid': 'end-time-input',
                inputMode: 'numeric',
                placeholder: 'HH:MM:SS',
              },
            }}
            sx={errors.shift_end_time ? errorBorderSx : undefined}
          />
        )}
      />
      <Controller
        name="retailer_id"
        control={control}
        render={({ field }) => (
          <TextField
            label="Retailer ID"
            size="small"
            {...field}
            error={!!errors.retailer_id}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { style: { height: 30, padding: '0 8px' } },
            }}
            disabled={retailerLocked}
            sx={{ mr: 1, ...(errors.retailer_id ? errorBorderSx : {}) }}
          />
        )}
      />
    </Box>
  </Box>
));

AuditDynamicFormHeader.displayName = 'AuditDynamicFormHeader';

export default AuditDynamicFormHeader;
